<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatCnpj } from '../composables/commissionPaymentValidation';
import SalesPartyPhoneField from './SalesPartyPhoneField.vue';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';

const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const identity = computed(() => props.profile.identity || {});
const fields = computed(() => [
  [
    'cnpj',
    t('SALES_PARTIES.FORM.CNPJ'),
    t('SALES_PARTIES.FORM.CNPJ_PLACEHOLDER'),
    'text',
  ],
  ['phone'],
  [
    'legal_name',
    t('SALES_PARTIES.FORM.LEGAL_NAME'),
    t('SALES_PARTIES.FORM.LEGAL_NAME_PLACEHOLDER'),
    'text',
  ],
  [
    'trade_name',
    t('SALES_PARTIES.FORM.TRADE_NAME'),
    t('SALES_PARTIES.FORM.TRADE_NAME_PLACEHOLDER'),
    'text',
  ],
]);

const inputValue = field =>
  field === 'cnpj' ? formatCnpj(identity.value[field]) : identity.value[field];
const fieldFilled = field => Boolean(String(inputValue(field) || '').trim());
const referenceControlClasses = ({ filled, error }) => {
  const classes = [
    'reset-base h-8 w-full border-0 border-b bg-transparent px-0 pr-7 text-base outline-none placeholder:text-[#A7B7BE] focus:ring-0',
  ];

  if (error) {
    classes.push('border-[#D64545] text-[#D64545]');
  } else if (filled) {
    classes.push('border-[#009F94] font-bold text-[#009F94]');
  } else {
    classes.push('border-[#A7B7BE] text-[#7F8C93]');
  }

  return classes;
};
const update = (field, value) =>
  emit(
    'update',
    'identity',
    field,
    field === 'cnpj' ? formatCnpj(value) : value
  );
</script>

<template>
  <section
    data-test="sales-party-company-section"
    class="rounded-[10px] bg-white p-8 font-roboto shadow-sm max-[700px]:p-0"
  >
    <header class="mb-6 max-[700px]:mb-11">
      <h2
        data-test="sales-party-company-title"
        class="font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
      >
        {{ t('SALES_PARTIES.COMPANY.FORM_TITLE') }}
      </h2>
      <p
        class="mt-2 w-[52vw] max-w-full text-xl font-normal leading-6 text-[#464F53] max-[700px]:w-full"
      >
        {{ t('SALES_PARTIES.COMPANY.FORM_DESCRIPTION') }}
      </p>
    </header>
    <div
      data-test="sales-party-company-fields"
      class="grid gap-y-8 min-[701px]:grid-cols-2 min-[701px]:gap-x-[14px]"
    >
      <div
        v-for="[field, label, placeholder, type] in fields"
        :key="field"
        :data-company-field="field"
        class="min-w-0"
      >
        <SalesPartyPhoneField
          v-if="field === 'phone'"
          :country-code="identity.country_code"
          :phone="identity.phone"
          :error="errors.phone"
          @update="(key, value) => emit('update', 'identity', key, value)"
        />
        <SalesPropertyReferenceField
          v-else
          :filled="fieldFilled(field)"
          :label="label"
          :error="errors[field]"
          required
          :test-id="`sales-party-company-${field}`"
        >
          <input
            :value="inputValue(field)"
            :type="type"
            :inputmode="field === 'cnpj' ? 'numeric' : undefined"
            :maxlength="field === 'cnpj' ? 18 : undefined"
            :placeholder="placeholder"
            :class="
              referenceControlClasses({
                error: errors[field],
                filled: fieldFilled(field),
              })
            "
            @input="update(field, $event.target.value)"
          />
        </SalesPropertyReferenceField>
      </div>
    </div>
  </section>
</template>
